import { Injectable, inject, signal, computed } from '@angular/core';
import { Clerk } from '@clerk/clerk-js';
import { dark } from '@clerk/themes';
import { environment } from '../../../environments/environment';
import { ThemeService } from './theme.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private themeService = inject(ThemeService);
  private clerk: InstanceType<typeof Clerk> | null = null;
  private _isLoaded = signal(false);
  private _isSignedIn = signal(false);
  private _user = signal<any>(null);
  private _orgRole = signal<string | null>(null);

  isLoaded = this._isLoaded.asReadonly();
  isSignedIn = this._isSignedIn.asReadonly();
  user = this._user.asReadonly();
  isOrgAdmin = computed(() => this._orgRole() === 'org:admin');

  userInitials = computed(() => {
    const u = this._user();
    if (!u) return '';
    const first = u.firstName?.[0] || '';
    const last = u.lastName?.[0] || '';
    return (first + last).toUpperCase() || u.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || '?';
  });

  userName = computed(() => {
    const u = this._user();
    if (!u) return '';
    if (u.firstName) return `${u.firstName} ${u.lastName || ''}`.trim();
    return u.primaryEmailAddress?.emailAddress || '';
  });

  async init(): Promise<void> {
    this.clerk = new Clerk(environment.clerkPublishableKey);
    await this.clerk.load();
    this._isLoaded.set(true);
    this.updateUserState();

    this.clerk.addListener(() => {
      this.updateUserState();
    });
  }

  private updateUserState(): void {
    this._isSignedIn.set(!!this.clerk?.user);
    this._user.set(this.clerk?.user || null);
    const activeOrgId = this.clerk?.organization?.id;
    const membership = this.clerk?.user?.organizationMemberships?.find(
      (m: any) => m.organization.id === activeOrgId
    );
    this._orgRole.set(membership?.role ?? null);
  }

  private appearance(extra: object = {}) {
    const isDark = this.themeService.isDark();
    return {
      baseTheme: isDark ? dark : undefined,
      variables: isDark ? {
        colorBackground: '#0f172a',   // surface-900 — sidebar bg
        colorInput: '#1e293b', // surface-800
        colorText: '#f1f5f9',         // surface-100
        colorTextSecondary: '#94a3b8', // surface-400
      } : {
        colorBackground: '#ffffff',   // white — main screen bg
        colorInput: '#f8fafc', // surface-50 — sidebar bg
        colorText: '#0f172a',         // surface-900
        colorTextSecondary: '#475569', // surface-600
      },
      ...extra,
    };
  }

  mountSignIn(element: HTMLDivElement): void {
    this.clerk?.mountSignIn(element, {
      appearance: this.appearance({
        elements: {
          rootBox: 'w-full',
          card: 'shadow-none w-full',
        }
      })
    });
  }

  unmountSignIn(element: HTMLDivElement): void {
    this.clerk?.unmountSignIn(element);
  }

  mountUserButton(element: HTMLDivElement): void {
    const appearance = this.appearance({
      elements: { avatarBox: 'w-8 h-8' }
    });
    this.clerk?.mountUserButton(element, {
      appearance,
      userProfileProps: { appearance },
    });
  }

  unmountUserButton(element: HTMLDivElement): void {
    this.clerk?.unmountUserButton(element);
  }

  async getToken(): Promise<string | null> {
    const session = this.clerk?.session;
    if (!session) return null;
    return session.getToken();
  }

  openOrganizationProfile(): void {
    this.clerk?.openOrganizationProfile({ appearance: this.appearance() });
  }

  openUserProfile(): void {
    this.clerk?.openUserProfile({ appearance: this.appearance() });
  }

  async signOut(): Promise<void> {
    await this.clerk?.signOut();
  }
}
